import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";

const MODULE_LABELS = {
  corsi: { label: "Corso", color: "#8e24aa", bg: "#f3e5f5" },
  extra_iscrizioni: { label: "Iscrizione Extra", color: "#1565c0", bg: "#e3f2fd" },
  famiglia_ricorrenti: { label: "Richiesta Famiglia", color: "#2e7d32", bg: "#e8f5e9" },
  plessi_config: { label: "Plesso Config", color: "#ef6c00", bg: "#fff3e0" },
};

const ACTION_LABELS = {
  create: { label: "Creato", icon: "➕", color: "#2e7d32" },
  update: { label: "Modificato", icon: "✏️", color: "#1565c0" },
  delete: { label: "Eliminato", icon: "🗑", color: "#c62828" },
  import: { label: "Import", icon: "📥", color: "#ef6c00" },
  export: { label: "Export", icon: "📤", color: "#0277bd" },
};

function formatTs(ts) {
  try {
    const d = new Date(ts);
    return d.toLocaleString("it-IT", { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit" });
  } catch (_e) { return ts; }
}

function DetailsCell({ details }) {
  if (!details || Object.keys(details).length === 0) return <span style={{ color: "#ccc" }}>—</span>;
  const parts = [];
  if (details.alunno) parts.push(`${details.alunno} (${details.alunno_cod || "?"})`);
  if (details.corso) parts.push(`corso: ${details.corso}`);
  if (details.anno) parts.push(`anno: ${details.anno}`);
  if (Array.isArray(details.giorni)) parts.push(`giorni: ${details.giorni.map(g => ["Lun","Mar","Mer","Gio","Ven"][g]).join("/")}`);
  if (Array.isArray(details.giorni_applicati)) parts.push(`giorni: ${details.giorni_applicati.map(g => ["Lun","Mar","Mer","Gio","Ven"][g]).join("/")}`);
  if (details.dal) parts.push(`dal: ${details.dal}`);
  if (details.note) parts.push(`note: ${details.note}`);
  if (details.descrizione) parts.push(`desc: ${details.descrizione}`);
  if (typeof details.inserite === "number") parts.push(`inserite: ${details.inserite}`);
  if (typeof details.giorno_aggiunto === "number") parts.push(`merge giorno: ${details.giorno_aggiunto}`);
  if (typeof details.gia_presenti === "number") parts.push(`invariate: ${details.gia_presenti}`);
  if (details.in_lista_attesa === true) parts.push("⏳ lista attesa");
  if (details.changes && Object.keys(details.changes).length) {
    const changeStr = Object.entries(details.changes).map(([k, v]) => {
      if (Array.isArray(v)) return `${k}=[${v.join(",")}]`;
      if (typeof v === "object" && v !== null) return `${k}=${JSON.stringify(v)}`;
      return `${k}=${v}`;
    }).join(", ");
    parts.push(`→ ${changeStr}`);
  }
  if (parts.length === 0) parts.push(JSON.stringify(details));
  return <span style={{ fontSize: 11, color: "#555" }}>{parts.join(" · ")}</span>;
}

export default function MensaAuditLogPage() {
  const [vista, setVista] = useState("leggibile");
  const [items, setItems] = useState([]);
  const [readable, setReadable] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterModule, setFilterModule] = useState("");
  const [filterAction, setFilterAction] = useState("");
  const [filterSearch, setFilterSearch] = useState("");
  const [dataDa, setDataDa] = useState("");
  const [dataA, setDataA] = useState("");

  const carica = async () => {
    setLoading(true);
    try {
      if (vista === "leggibile") {
        const params = { limit: 1000 };
        if (filterSearch.trim()) params.q = filterSearch.trim();
        const r = await api.get("/mensa-matrice/log-attivita", { params });
        setReadable(r.data.items || []);
      } else {
        const params = {};
        if (filterModule) params.module = filterModule;
        if (filterAction) params.action = filterAction;
        params.limit = 1000;
        const r = await api.get("/mensa-matrice/audit-log", { params });
        setItems(r.data.items || []);
      }
    } catch (_e) { setItems([]); setReadable([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { carica(); /* eslint-disable-next-line */ }, [vista, filterModule, filterAction]);

  const filtered = useMemo(() => {
    const s = filterSearch.trim().toLowerCase();
    if (!s) return items;
    return items.filter(it => JSON.stringify(it).toLowerCase().includes(s));
  }, [items, filterSearch]);

  const users = useMemo(() => {
    const src = vista === "leggibile" ? readable : items;
    const set = new Set(src.map(it => it.user).filter(Boolean));
    return Array.from(set).sort();
  }, [items, readable, vista]);

  const isReadable = vista === "leggibile";

  const readableFiltered = useMemo(() => {
    return (readable || []).filter(it => {
      if (!it.ts) return true;
      const d = it.ts.slice(0, 10); // YYYY-MM-DD
      if (dataDa && d < dataDa) return false;
      if (dataA && d > dataA) return false;
      return true;
    });
  }, [readable, dataDa, dataA]);

  const stampaLeggibile = () => {
    const fmt = (ts) => { try { return new Date(ts).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch (_e) { return ts; } };
    const periodo = (dataDa || dataA) ? ` — periodo ${dataDa ? dataDa.split("-").reverse().join("/") : "…"} → ${dataA ? dataA.split("-").reverse().join("/") : "…"}` : "";
    const cerca = filterSearch.trim() ? ` — filtro "${filterSearch.trim()}"` : "";
    const rows = readableFiltered.map((it, i) => `
      <tr>
        <td style="padding:4px 8px;border-bottom:1px solid #eee;font-family:monospace;font-size:10px;white-space:nowrap;">${fmt(it.ts)}</td>
        <td style="padding:4px 8px;border-bottom:1px solid #eee;font-size:12px;color:${it.tipo === "attivato" ? "#1b5e20" : "#b71c1c"};font-weight:600;">${it.tipo === "attivato" ? "✔" : "✘"} ${(it.testo || "").replace(/</g, "&lt;")}</td>
        <td style="padding:4px 8px;border-bottom:1px solid #eee;font-size:10px;color:#666;">${it.user || "system"}</td>
      </tr>`).join("");
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Log attività Matrice Mensa</title></head>
      <body style="font-family:Arial,sans-serif;margin:24px;">
        <h2 style="color:#1f3864;margin:0 0 4px;">Log attività — Matrice Mensa / Extra / SPOT</h2>
        <div style="font-size:11px;color:#666;margin-bottom:12px;">${readableFiltered.length} righe${periodo}${cerca} · stampato il ${new Date().toLocaleString("it-IT")}</div>
        <table style="width:100%;border-collapse:collapse;border:1px solid #ccc;">
          <thead><tr style="background:#1f3864;color:#fff;">
            <th style="padding:6px 8px;text-align:left;font-size:11px;">DATA / ORA</th>
            <th style="padding:6px 8px;text-align:left;font-size:11px;">ATTIVITÀ</th>
            <th style="padding:6px 8px;text-align:left;font-size:11px;">UTENTE</th>
          </tr></thead>
          <tbody>${rows}</tbody>
        </table>
      </body></html>`;
    const w = window.open("", "_blank");
    if (!w) return;
    w.document.write(html);
    w.document.close();
    w.focus();
    setTimeout(() => { w.print(); }, 300);
  };

  return (
    <div className="sw-container" data-testid="mensa-audit-log-page">
      <div className="sw-section-header" style={{ padding: "12px 20px", background: "#f5f5f5", borderBottom: "2px solid #ccc" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
          <h2 style={{ margin: 0, fontSize: 16, color: "#1f3864" }}>📋 Log attività — Matrice Mensa / Extra / SPOT</h2>
          <div data-testid="audit-vista-toggle" style={{ display: "inline-flex", border: "1px solid #1565c0", borderRadius: 6, overflow: "hidden" }}>
            <button onClick={() => setVista("leggibile")} data-testid="audit-vista-leggibile"
              style={{ padding: "5px 14px", border: "none", cursor: "pointer", fontSize: 12, fontWeight: 700,
                       background: isReadable ? "#1565c0" : "white", color: isReadable ? "white" : "#1565c0" }}>📖 Leggibile</button>
            <button onClick={() => setVista("tecnico")} data-testid="audit-vista-tecnico"
              style={{ padding: "5px 14px", border: "none", cursor: "pointer", fontSize: 12, fontWeight: 700,
                       background: !isReadable ? "#1565c0" : "white", color: !isReadable ? "white" : "#1565c0" }}>🔧 Tecnico</button>
          </div>
        </div>
        <div style={{ fontSize: 11, color: "#666", marginTop: 4 }}>
          {isReadable
            ? "Cosa è cambiato nella matrice mensa, in linguaggio naturale (es. \"Attivato ROSSI MARIO il martedì per adesione al corso PIANOFORTE\"). Ordinato dal più recente."
            : "Cronologia tecnica di tutte le modifiche a corsi, iscrizioni extra e richieste famiglie. Ordinato dal più recente."}
        </div>
      </div>

      <div style={{ padding: "10px 20px", display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", background: "#fafafa", borderBottom: "1px solid #eee" }}>
        {!isReadable && (
          <>
            <select value={filterModule} onChange={e => setFilterModule(e.target.value)}
              data-testid="audit-filter-module"
              style={{ padding: "5px 8px", border: "1px solid #ccc", fontSize: 12 }}>
              <option value="">Tutti i moduli</option>
              <option value="corsi">Corsi</option>
              <option value="extra_iscrizioni">Iscrizioni Extra</option>
              <option value="famiglia_ricorrenti">Richieste Famiglie</option>
              <option value="plessi_config">Plessi Config</option>
            </select>
            <select value={filterAction} onChange={e => setFilterAction(e.target.value)}
              data-testid="audit-filter-action"
              style={{ padding: "5px 8px", border: "1px solid #ccc", fontSize: 12 }}>
              <option value="">Tutte le azioni</option>
              <option value="create">Creati</option>
              <option value="update">Modificati</option>
              <option value="delete">Eliminati</option>
              <option value="import">Import</option>
              <option value="export">Export</option>
            </select>
          </>
        )}
        <input type="text" value={filterSearch} onChange={e => setFilterSearch(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter" && isReadable) carica(); }}
          placeholder={isReadable ? "Cerca alunno, corso, giorno... (Invio)" : "Cerca in alunno, corso, utente, note..."}
          data-testid="audit-search"
          style={{ padding: "5px 10px", border: "1px solid #ccc", fontSize: 12, flex: 1, minWidth: 200 }} />
        {isReadable && (
          <>
            <label style={{ fontSize: 11, color: "#666" }}>Dal
              <input type="date" value={dataDa} onChange={e => setDataDa(e.target.value)} data-testid="readable-data-da"
                style={{ padding: "4px 6px", border: "1px solid #ccc", fontSize: 12, marginLeft: 4 }} />
            </label>
            <label style={{ fontSize: 11, color: "#666" }}>Al
              <input type="date" value={dataA} onChange={e => setDataA(e.target.value)} data-testid="readable-data-a"
                style={{ padding: "4px 6px", border: "1px solid #ccc", fontSize: 12, marginLeft: 4 }} />
            </label>
            {(dataDa || dataA) && (
              <button onClick={() => { setDataDa(""); setDataA(""); }} data-testid="readable-data-reset"
                style={{ padding: "5px 10px", background: "#eceff1", color: "#455a64", border: "1px solid #cfd8dc", fontSize: 12, cursor: "pointer" }}>✕ Date</button>
            )}
            <button onClick={stampaLeggibile} data-testid="readable-print"
              disabled={readableFiltered.length === 0}
              style={{ padding: "5px 14px", background: readableFiltered.length === 0 ? "#bdbdbd" : "#2e7d32", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: readableFiltered.length === 0 ? "default" : "pointer" }}>🖨 Stampa / Esporta</button>
          </>
        )}
        <button onClick={carica} data-testid="audit-refresh"
          style={{ padding: "5px 14px", background: "#1565c0", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>🔄 Aggiorna</button>
        <div style={{ fontSize: 11, color: "#666", marginLeft: "auto" }}>
          <b>{isReadable ? readableFiltered.length : filtered.length}</b> righe · <b>{users.length}</b> utenti distinti
        </div>
      </div>

      {isReadable ? (
        <div style={{ margin: "0 20px 20px", background: "white", border: "1px solid #ccc", maxHeight: "72vh", overflowY: "auto" }}>
          {loading && <div style={{ textAlign: "center", padding: 20, color: "#888" }}>Caricamento...</div>}
          {!loading && readableFiltered.length === 0 && (
            <div style={{ textAlign: "center", padding: 20, color: "#888" }}>Nessuna attività registrata. Le nuove iscrizioni/ritiri e le richieste SPOT compariranno qui.</div>
          )}
          {!loading && readableFiltered.map((it, i) => {
            const attivo = it.tipo === "attivato";
            return (
              <div key={i} data-testid={`readable-row-${i}`}
                style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "9px 16px",
                         borderBottom: "1px solid #f0f0f0", borderLeft: `4px solid ${attivo ? "#2e7d32" : "#c62828"}` }}>
                <span style={{ fontSize: 18, lineHeight: "20px" }}>{attivo ? "✅" : "⛔"}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, color: "#212121", fontWeight: 600 }}>{it.testo}</div>
                  <div style={{ fontSize: 10, color: "#9e9e9e", marginTop: 2, fontFamily: "Consolas, monospace" }}>
                    {formatTs(it.ts)} · 👤 {it.user || "system"}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
      <div style={{ margin: "0 20px 20px", background: "white", border: "1px solid #ccc", maxHeight: "72vh", overflowY: "auto" }}>
        <table className="sw-table sw-table-compact" style={{ fontSize: 11, width: "100%" }}>
          <thead style={{ background: "#1f3864", color: "white", position: "sticky", top: 0, zIndex: 1 }}>
            <tr>
              <th style={{ width: 150, textAlign: "left", padding: "6px 8px" }}>DATA / ORA</th>
              <th style={{ width: 130, textAlign: "left", padding: "6px 8px" }}>UTENTE</th>
              <th style={{ width: 150, textAlign: "center", padding: "6px 8px" }}>MODULO</th>
              <th style={{ width: 110, textAlign: "center", padding: "6px 8px" }}>AZIONE</th>
              <th style={{ width: 140, padding: "6px 8px" }}>TARGET</th>
              <th style={{ padding: "6px 8px" }}>DETTAGLIO</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} style={{ textAlign: "center", padding: 20, color: "#888" }}>Caricamento...</td></tr>}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={6} style={{ textAlign: "center", padding: 20, color: "#888" }}>Nessun log con questi filtri.</td></tr>
            )}
            {!loading && filtered.map((it, i) => {
              const mod = MODULE_LABELS[it.module] || { label: it.module, color: "#666", bg: "#f5f5f5" };
              const act = ACTION_LABELS[it.action] || { label: it.action, icon: "•", color: "#666" };
              // TARGET leggibile: per iscrizioni/famiglia mostra l'anagrafica invece dell'ObjectId
              let targetDisplay = it.target || "—";
              let targetSub = "";
              if (it.module === "extra_iscrizioni" && it.details?.alunno) {
                targetDisplay = it.details.alunno;
                targetSub = `#${it.details.alunno_cod || "?"}${it.details.corso ? ` · ${it.details.corso}` : ""}`;
              } else if (it.module === "famiglia_ricorrenti" && it.details?.alunno) {
                targetDisplay = it.details.alunno;
                targetSub = `#${it.details.alunno_cod || "?"}`;
              } else if (it.module === "corsi" && it.target && it.target !== "*") {
                targetDisplay = it.target;
                targetSub = it.details?.descrizione || "";
              } else if (it.target === "*") {
                targetDisplay = "(TUTTI)";
              }
              return (
                <tr key={i} data-testid={`audit-row-${i}`}>
                  <td style={{ fontSize: 11, fontFamily: "Consolas, monospace", color: "#333", padding: "4px 8px" }}>{formatTs(it.ts)}</td>
                  <td style={{ fontWeight: 700, color: "#0d47a1", padding: "4px 8px" }}>{it.user || "—"}</td>
                  <td style={{ textAlign: "center", padding: "4px 8px" }}>
                    <span style={{ padding: "2px 8px", background: mod.bg, color: mod.color, fontSize: 10, fontWeight: 700, borderRadius: 2 }}>{mod.label}</span>
                  </td>
                  <td style={{ textAlign: "center", color: act.color, fontWeight: 700, padding: "4px 8px" }}>
                    {act.icon} {act.label}
                  </td>
                  <td style={{ fontSize: 10, padding: "4px 8px" }}>
                    <div style={{ fontWeight: 700, color: "#263238" }}>{targetDisplay}</div>
                    {targetSub && <div style={{ color: "#888", fontFamily: "Consolas, monospace", fontSize: 9 }}>{targetSub}</div>}
                  </td>
                  <td style={{ padding: "4px 8px" }}><DetailsCell details={it.details} /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      )}
    </div>
  );
}
